import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import AssignedPricing from "@/components/AssignedPricing";
import { toast } from "sonner";
import { CurrencyDollar, FloppyDisk, UsersThree } from "@phosphor-icons/react";

const toMinor = (v) => (v === "" || v == null ? null : Math.round(parseFloat(v) * 100));
const toDollars = (m) => (m == null ? "" : (m / 100).toFixed(2));

const CUSTOMER_FIELDS = [
  { key: "standard_customer_price_minor", label: "Concurrent Call Limit Price",
    unit: "Price per concurrent channel (USD)",
    desc: "Charged when your customer buys additional concurrent call channels." },
  { key: "standard_customer_ai_model_price_minor", label: "AI Model per min price",
    unit: "Price per minute (USD)",
    desc: "Per-minute rate for AI model usage on your customers' calls." },
  { key: "standard_customer_tts_price_minor", label: "TTS per min price",
    unit: "Price per minute (USD)",
    desc: "Per-minute rate for text-to-speech used by your customers." },
  { key: "standard_customer_stt_price_minor", label: "STT per min price",
    unit: "Price per minute (USD)",
    desc: "Per-minute rate for speech-to-text used by your customers." },
];

const EMPTY = Object.fromEntries(CUSTOMER_FIELDS.map((f) => [f.key, ""]));

function PriceRow({ testid, title, desc, unit, value, onChange, onSave, busy }) {
  return (
    <Card className="p-5 rounded-xl border-border" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-accent grid place-items-center shrink-0">
          <CurrencyDollar className="w-5 h-5 text-primary" weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm sm:text-base">{title}</h3>
          <p className="text-sm text-muted-foreground mt-0.5">{desc}</p>
          <div className="mt-3 flex items-end gap-3">
            <div className="space-y-1.5 flex-1 max-w-[220px]">
              <Label className="text-xs">{unit}</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input data-testid={`${testid}-input`} type="number" min="0" step="0.01"
                  className="pl-7" value={value}
                  onChange={(e) => onChange(e.target.value)} placeholder="0.00" />
              </div>
            </div>
            <Button data-testid={`${testid}-save`} onClick={onSave} disabled={busy}
              className="rounded-lg">
              <FloppyDisk className="w-4 h-4 mr-1.5" weight="bold" /> Save
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function ResellerPricing() {
  const [mine, setMine] = useState(null);
  const [values, setValues] = useState(EMPTY);
  const [source, setSource] = useState("platform");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [my, cust] = await Promise.all([
        api.get("/billing/my-pricing"),
        api.get("/billing/customer-price"),
      ]);
      setMine(my.data || null);
      const next = { ...EMPTY };
      for (const f of CUSTOMER_FIELDS) {
        // Prefer own saved value; fall back to effective (platform) for display/edit
        const own = cust.data?.[`${f.key}_own`];
        next[f.key] = toDollars(own != null ? own : cust.data?.[f.key]);
      }
      setValues(next);
      setSource(cust.data?.source || "platform");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (key) => {
    const minor = toMinor(values[key]);
    if (minor == null || isNaN(minor) || minor < 0) {
      toast.error("Enter a valid non-negative price.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put("/billing/customer-price", { [key]: minor });
      const next = { ...EMPTY };
      for (const f of CUSTOMER_FIELDS) {
        const own = data?.[`${f.key}_own`];
        next[f.key] = toDollars(own != null ? own : data?.[f.key]);
      }
      setValues(next);
      setSource(data?.source || "reseller");
      toast.success("Customer price saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="reseller-pricing-page">
      <PageHeader title="Pricing"
        subtitle="Your assigned reseller rates, plus the standard customer prices you apply when adding customers." />

      <AssignedPricing
        pricing={mine}
        loading={loading}
        title="Your assigned reseller pricing"
        subtitle="Rates assigned to your reseller account."
        testid="reseller-own-pricing"
      />

      <section className="space-y-4" data-testid="reseller-customer-standards">
        <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 grid place-items-center shrink-0">
            <UsersThree className="w-5 h-5 text-primary" weight="duotone" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Standard customer pricing</h2>
            <p className="text-sm text-muted-foreground">
              Set the rates applied when you add a customer with Standard pricing.
              Existing customers keep their already-assigned prices.
              {source === "platform" && !loading && (
                <span className="block mt-1 text-xs">Currently inheriting platform defaults — save a value to override.</span>
              )}
            </p>
          </div>
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="grid gap-3 max-w-3xl">
            {CUSTOMER_FIELDS.map((f) => (
              <PriceRow
                key={f.key}
                testid={`reseller-customer-${f.key}`}
                title={f.label}
                desc={f.desc}
                unit={f.unit}
                value={values[f.key]}
                onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
                onSave={() => save(f.key)}
                busy={busy}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
